import { Button, Input, Label, Switch, Textarea } from "@/components/ui"
import { TooltipInfo } from "@/components/ui/TooltipInfo"
import { useEffect, useState } from "react";
import { useEmailTypeSettings, useUpdateEmailTypeSettings } from "@/hooks/useEmailSettings";
import { Loader2 } from "lucide-react";
import { Example } from "./ExampleContent";
import { TimeSelect } from "../components/TimeSelect";

type TimeUnit = "minutes" | "hours" | "days";

export const PreCallNotificationEmail = () => {
  const [subject, setSubject] = useState('');
  const [prompt, setPrompt] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [timings, setTimings] = useState<{value: number, unit: TimeUnit}[]>([{value: 15, unit: 'minutes'}]);
    const { data: email } = useEmailTypeSettings('pre_meeting');
  const { mutate: updateEmailSettings, isPending } = useUpdateEmailTypeSettings();
  useEffect(() => {
    setSubject(email?.pre_meeting?.subject || '');
    setPrompt(email?.pre_meeting?.prompt || '');
    setTimings(email?.pre_meeting?.timings || [{value: 15, unit: 'minutes'}]);
    if (email?.pre_meeting?.enabled !== undefined) setEnabled(email.pre_meeting.enabled);
  }, [email?.pre_meeting?.subject, email?.pre_meeting?.prompt, email?.pre_meeting?.timings]);

  const isChanged  = (subject.trim() !== email?.pre_meeting?.subject?.trim() && subject.trim() !== '') || 
                     (prompt !== email?.pre_meeting?.prompt) ||
                     (JSON.stringify(timings) !== JSON.stringify(email?.pre_meeting?.timings || [{value: 15, unit: 'minutes'}]));

  const handleToggleEnabled = (newStatus: boolean) => {
    updateEmailSettings(
      { type: 'pre_meeting', settings: { subject, prompt, timings, enabled: newStatus } },
      { onSuccess: () => setEnabled(newStatus) }
    );
  }

  const cancel = () => {
    setSubject(email?.pre_meeting?.subject || '');
    setPrompt(email?.pre_meeting?.prompt || '');
    setTimings(email?.pre_meeting?.timings || [{value: 15, unit: 'minutes'}]);
    setEnabled(email?.pre_meeting?.enabled || false);
  }

  return (  
    <div className="col-span-3 relative">
      <div className="flex items-start justify-between ">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
          Pre-Call Notification Email Settings
            {!enabled && <div className={`px-2  flex items-center font-medium justify-center text-xs text-white rounded-full bg-gray-300`}>
               Disabled
            </div>}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 italic mb-10">Lead will automatically receive a pre-call email before their appointment.</p>
        </div>
        <Switch checked={enabled} onCheckedChange={handleToggleEnabled} disabled={isPending} />
      </div>
      {!enabled && <div className="absolute top-20 right-0 left-0 bottom-0 bg-white/80 z-10"/>}
      <div className="flex flex-col gap-10">
        <div>
          <div className="flex items-center mb-1 gap-2">
            <Label htmlFor="subject" className="font-semibold text-gray-900 text-base">Subject</Label>
            <TooltipInfo description="Set your own subject of the cancellation email." />
          </div>
          <Input id="subject" placeholder="Subject" className="mt-1 h-12 block w-full" type="text" value={subject} onChange={(e) => setSubject(e.target.value)} />
        </div>
        <div>
          <div className="flex items-center mb-1 gap-2">
            <Label htmlFor="body" className="font-semibold text-gray-900 text-base">Body</Label>
            <TooltipInfo description="Provide clear instructions and restrictions for the AI assistant on how to generate the email content." />
          </div>
          <Textarea id="body" rows={4} placeholder="AI email generation instructions" className="mt-1 block w-full" value={prompt} onChange={(e) => setPrompt(e.target.value)} />
        </div>
        <div>
          <div className="flex items-center mb-1 gap-2">
            <p  className="font-semibold text-gray-900 text-base">Timing</p>
            <TooltipInfo description="Settings for automatically sending call notifications a specified time before the call starts. When leads are uploaded, emails are sent immediately. However, when attempting to call again, we can set the time for sending the email before the next call." />
          </div>
          <p className="font-light  text-gray-500 text-xs mb-2">
          Emails are sent at least 24 hours apart. Next emails are sent at the specified time before the retry call. The maximum number of emails per lead is 3.
          </p>
          <div className="flex flex-col gap-2"> 
            <TimeSelect 
              value={timings[0]}
              onChange={(value) => setTimings([value])}
              onRemove={() => setTimings([])}
              isFirst={true}
              description="Sends before the next call."
            />
          </div>
        </div>
        <Example prompt={prompt} type="pre_call" />
      </div>
      <div className="space-y-4 mb-8 flex flex-col gap-2 mt-10">
      {isChanged && 
        <div className="flex gap-2 mt-20 ">
          <Button 
            variant="default" 
            className="w-[100px]" 
            onClick={()=> updateEmailSettings({ type: 'pre_meeting', settings: { subject, prompt, timings, enabled } })} 
            disabled={isPending}
          >
            {isPending 
              ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Save</>) 
              : ('Save')
            }
          </Button>
          <Button variant="outline" className="px-6" onClick={cancel}>
            Cancel
          </Button>
        </div>
      }
      </div>

    </div>
  )
}